import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
// import axios from './axios-config';

interface FetchUsersPayload {
	url: string
}

export interface UsersListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: UsersListResponse | []
}

export interface AuthResponse {
	response: string
}

interface AuthPayload {
	organizationType: string
	organizationId: string
}

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface PermissionState {
	permissionName: string | undefined
	enabled: boolean
	editable: boolean
}

interface UserCorporates {
	createdBy: string | undefined
	creationDate: string | undefined
	id: number
	modificationDate: string | undefined
	modifiedBy: string | undefined
	version: number
	corporateSenderId: string | undefined
	read: boolean
	write: boolean
}

export interface UserState {
	id: number
	idBank: string | undefined
	bankName: string | undefined
	corporateSenderId: string | undefined
	corporateName: string | undefined
	fullName: string
	description: string | undefined
	username: string
	email: string
	phone: string | undefined
	enabled: boolean
	reset: boolean
	language: string | undefined
	image: null
	last_login: string | undefined
	failedLoginCount: number | undefined
	reportSection: boolean
	distinteSection: boolean
	job: boolean
	roles: string[]
	permissions: PermissionState[]
	userCorporates: UserCorporates[]
}

export interface PageableElement {
	content: UserState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: UserState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface UsersListResponse {
	nextUrl: null | string
	page: PageElement
}

export interface ProfileData {
	phone?: string | undefined
	currentPassword?: string | undefined
	newPassword?: string | undefined
	repeatPassword?: string | undefined
	language: string | undefined
	avatar?: undefined | File
}

export interface UserData {
	idBank: string | undefined
	corporateSenderId: string | undefined
	fullName: string
	description: string
	enabled: boolean
	username: string
	phone: string | undefined
	email: string
	reset: boolean
	password?: string | undefined
	role: string
	language: string
	bankName: string
	corporateName: string
	reportSection: boolean
	distinteSection: boolean
	permissions: PermissionState[] | undefined
}

export interface ICreateResponse {}

export interface ICreatePayload {
	userData: UserData
}

export interface IModifyProfilePayload {
	profileData: ProfileData
}

interface RolePermissionsPayload {
	role: string
}

export interface Permission {
	id: number
	role: string
	enabled: boolean
	editable: boolean
	permission: {
		id: number
		name: string
	}
}
export interface RolePermissionsResponse {
	data: Permission[]
}

interface IPatchPayload {
	idUser: string
	userData: {
		fullName?: string
		description?: string
		enabled?: boolean
		phone?: string | undefined
		reset?: boolean
		language?: string
		reportSection?: boolean
		distinteSection?: boolean
	}
}

export const getRolePermissions = createAsyncThunk<
	RolePermissionsResponse,
	RolePermissionsPayload,
	{ rejectValue: string }
>(
	"user/permissions",
	async ({ role }: RolePermissionsPayload, { rejectWithValue }) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse<RolePermissionsResponse> = await axios.get(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apipermission/permissions/list/${role}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<string>
			return rejectWithValue(axiosError?.response?.data ?? "generic")
		}
	}
)

export const getAllowedDomain = createAsyncThunk<
	AuthResponse,
	AuthPayload,
	{ rejectValue: string }
>(
	"user/domains-list",
	async (
		{ organizationType, organizationId }: AuthPayload,
		{ rejectWithValue }
	) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse<AuthResponse> = await axios.get(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apialloweddomain/domains/list/${organizationType}/${organizationId}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<string>

			return rejectWithValue(axiosError?.response?.data ?? "generic")
		}
	}
)

export interface UsersPayload {
	filter?: any | undefined
	sort?: any | undefined
	page?: number | undefined
	pageSize?: number | undefined
}

let cancelTokenFetch = null

export const getUsersPaginated = createAsyncThunk<
	UsersListResponse,
	UsersPayload,
	{ rejectValue: string }
>(
	"user/getUsersPaginated",
	async (
		{ page, pageSize, filter, sort }: UsersPayload,
		{ dispatch, rejectWithValue }
	) => {
		try {
			if (cancelTokenFetch) {
				cancelTokenFetch.cancel("Operazione cancellata")
				cancelTokenFetch = null
			}

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiuser/v2/users`
			const parameters = new URLSearchParams()

			if (page === 0 || page) {
				parameters.append("page", page.toString())
			}
			if (pageSize) {
				parameters.append("size", pageSize.toString())
			}
			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<UsersListResponse> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<UsersListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const fetchUsers = createAsyncThunk<
	UsersListResponse,
	FetchUsersPayload,
	{ rejectValue: string }
>(
	"banks/fetchUsers",
	async ({ url }: FetchUsersPayload, { _dispatch, rejectWithValue }) => {
		try {
			cancelTokenFetch = axios.CancelToken.source()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				cancelToken: cancelTokenFetch.token,
				withCredentials: true,
			}
			const response: AxiosResponse<UsersListResponse> = await axios.get(
				url,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<UsersListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const createUser = createAsyncThunk<
	ICreateResponse,
	ICreatePayload,
	{ rejectValue: AxiosError }
>("user/create", async ({ userData }: ICreatePayload, { rejectWithValue }) => {
	try {
		const headerConfig: AxiosRequestConfig = {
			headers: {
				"Content-Type": "application/json",
			},
			withCredentials: true,
		}
		const response: AxiosResponse = await axios.post(
			`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiuser/v2/create`,
			userData,
			headerConfig
		)
		return response
	} catch (error) {
		return rejectWithValue(error?.response)
	}
})

export const updateUser = createAsyncThunk<
	ICreateResponse,
	IPatchPayload,
	{ rejectValue: AxiosError }
>(
	"user/patch",
	async ({ userData, idUser }: IPatchPayload, { rejectWithValue }) => {
		try {
			const headerConfig: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse = await axios.patch(
				`${process.env.REACT_APP_USER_MANAGEMENT_API_URL}/userservice/apiuser/v2/update/${idUser}`,
				userData,
				headerConfig
			)
			return response
		} catch (error) {
			return rejectWithValue(error?.response)
		}
	}
)
